import type React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import TailwindSlider from "~/components/ui/slider";
import { TOAST_MESSAGES } from "~/constants/messages";
import { useOrganization } from "~/hooks/useOrganization";
import ReloadIcon from "~/images/reload.svg?react";
import type { RootState } from "~/store";

type PlanType = "free" | "starter" | "standard" | "professional" | "enterprise";

const planLabels: Record<PlanType, string> = {
  free: "フリー",
  starter: "スターター",
  standard: "スタンダード",
  professional: "プロフェッショナル",
  enterprise: "エンタープライズ",
};

interface CreditSelectorProps {
  variant?: "compact" | "full";
}

const CreditSelector: React.FC<CreditSelectorProps> = ({ variant = "full" }) => {
  const { currentUser, organization } = useSelector((state: RootState) => state.auth);
  const [plan, setPlan] = useState<PlanType>("free");
  const [remainCredit, setRemainCredit] = useState<number>(0);
  const [monthlyCredit, setMonthlyCredit] = useState<number>(0);

  const { isLoading, refresh } = useOrganization(currentUser?.organization_id);

  const handleReload = async () => {
    if (!organization?.id) {
      toast.error(TOAST_MESSAGES.LAYOUT.CREDIT_ORGANIZATION_NOT_FOUND);
      return;
    }
    const ok = await refresh();
    if (!ok) {
      toast.error(TOAST_MESSAGES.LAYOUT.CREDIT_UPDATE_FAILED);
    }
    // 成功時のローカル state 更新は不要。
    // 既存の useEffect([organization]) が organization の変更を検知して自動同期するため。
  };

  useEffect(() => {
    if (organization) {
      setPlan(organization.plan?.type || "free");
      setRemainCredit(organization.remainingCredit || 0);
      setMonthlyCredit(
        organization.plan.type !== "free" ? organization.plan.limits.monthlyLimit : 150,
      );
    }
  }, [organization]);

  if (!organization) {
    return null;
  }

  if (variant === "compact") {
    return (
      <div
        className="flex flex-col items-start bg-white rounded-md shadow p-[12px] gap-[8px] w-full"
        style={{ minWidth: "240px" }}
      >
        {!isLoading && (
          <div className="appearance-none bg-bglightblue text-lightblue text-sm font-bold rounded-md px-2 py-1 border-none focus:outline-none">
            {planLabels[plan]}
          </div>
        )}

        <div className="flex items-center gap-[8px] w-full">
          <span className="text-black whitespace-nowrap font-bold text-sm">クレジット</span>
          <div className="flex items-center gap-[4px]">
            <span className="text-lightblue font-bold text-sm">
              {isLoading ? "" : remainCredit}
            </span>
            <span className="text-gray font-medium text-sm">/{isLoading ? "" : monthlyCredit}</span>
          </div>
          <div onClick={handleReload} style={{ cursor: "pointer", marginLeft: "auto" }}>
            <ReloadIcon width={16} height={16} />
          </div>
        </div>

        <div className="w-full">
          <TailwindSlider
            value={isLoading ? 0 : remainCredit}
            max={isLoading ? 1 : monthlyCredit}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-grow items-center bg-white rounded-md shadow p-[8px] gap-[12px] w-full h-[45px]">
      {!isLoading && (
        <div className="appearance-none bg-bglightblue text-lightblue text-sm font-bold rounded-md px-2 py-1 height-full text-base border-none focus:outline-none">
          {planLabels[plan]}
        </div>
      )}

      {/* Remaining Credit */}
      <div className="flex flex-col flex-grow min-w-[144px] w-auto gap-[2px]">
        <div className="flex flex-grow items-center justify-between text-xs text-black font-medium mb-1">
          <span className="text-black whitespace-nowrap pr-2 font-bold">クレジット </span>
          <div className="flex items-center gap-[4px]">
            <div>
              <span className="text-lightblue font-bold">{isLoading ? "" : remainCredit}</span>
              <span className="text-gray font-medium pl-1">/{isLoading ? "" : monthlyCredit}</span>
            </div>
            <div onClick={handleReload} style={{ cursor: "pointer" }}>
              <ReloadIcon width={16} height={16} />
            </div>
          </div>
        </div>
        <TailwindSlider value={isLoading ? 0 : remainCredit} max={isLoading ? 1 : monthlyCredit} />
      </div>
    </div>
  );
};

export default CreditSelector;
